import { Fragment, useId, useRef, type ReactElement, type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Icon, type IconName } from './icons';
import { Button, cx } from './primitives';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

export function Modal({ open, onOpenChange, title, description, children, footer, className }: ModalProps) {
  const descriptionId = useId();
  const previousFocus = useRef<HTMLElement | null>(null);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="sv-modal-overlay" />
        <DialogPrimitive.Content
          className={cx('sv-modal', className)}
          aria-describedby={description ? descriptionId : undefined}
          onOpenAutoFocus={() => {
            previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          }}
          onCloseAutoFocus={(event) => {
            if (previousFocus.current?.isConnected) {
              event.preventDefault();
              previousFocus.current.focus({ preventScroll: true });
            }
          }}
        >
          <div className="sv-modal__header">
            <div className="sv-modal__heading">
              <DialogPrimitive.Title className="sv-modal__title">{title}</DialogPrimitive.Title>
              {description && <DialogPrimitive.Description id={descriptionId} className="sv-modal__description">{description}</DialogPrimitive.Description>}
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Закрыть окно" className="sv-modal__close"><Icon name="page-close" /></Button>
            </DialogPrimitive.Close>
          </div>
          <div className="sv-modal__body">{children}</div>
          {footer && <div className="sv-modal__footer">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export interface DropdownItem {
  id: string;
  label: ReactNode;
  icon?: IconName;
  onSelect?: () => void;
  disabled?: boolean;
  danger?: boolean;
  separatorBefore?: boolean;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export interface DropdownProps {
  trigger: ReactElement;
  items: DropdownItem[];
  label?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
}

export function Dropdown({ trigger, items, label, align = 'end', side = 'bottom', className }: DropdownProps) {
  return (
    <DropdownPrimitive.Root>
      <DropdownPrimitive.Trigger asChild>{trigger}</DropdownPrimitive.Trigger>
      <DropdownPrimitive.Portal>
        <DropdownPrimitive.Content className={cx('sv-dropdown', className)} align={align} side={side} sideOffset={6} collisionPadding={12} aria-label={label}>
          {label && <DropdownPrimitive.Label className="sv-dropdown__label">{label}</DropdownPrimitive.Label>}
          {items.map((item) => <Fragment key={item.id}>
            {item.separatorBefore && <DropdownPrimitive.Separator className="sv-dropdown__separator" />}
            {typeof item.checked === 'boolean' ? (
              <DropdownPrimitive.CheckboxItem
                checked={item.checked}
                disabled={item.disabled}
                onCheckedChange={(checked) => item.onCheckedChange?.(checked === true)}
                onSelect={(event) => { event.preventDefault(); item.onSelect?.(); }}
                className={cx('sv-dropdown__item', 'sv-dropdown__item--check', item.danger && 'sv-dropdown__item--danger')}
              >
                <span className="sv-dropdown__indicator"><DropdownPrimitive.ItemIndicator><Icon name="check" /></DropdownPrimitive.ItemIndicator></span>
                <span>{item.label}</span>
              </DropdownPrimitive.CheckboxItem>
            ) : (
              <DropdownPrimitive.Item disabled={item.disabled} onSelect={item.onSelect} className={cx('sv-dropdown__item', item.danger && 'sv-dropdown__item--danger')}>
                {item.icon && <Icon name={item.icon} />}
                <span>{item.label}</span>
              </DropdownPrimitive.Item>
            )}
          </Fragment>)}
        </DropdownPrimitive.Content>
      </DropdownPrimitive.Portal>
    </DropdownPrimitive.Root>
  );
}

export interface PopoverProps {
  trigger: ReactElement;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  label?: string;
}

export function Popover({ trigger, children, open, onOpenChange, align = 'start', side = 'bottom', className, label }: PopoverProps) {
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content className={cx('sv-popover', className)} align={align} side={side} sideOffset={7} collisionPadding={12} aria-label={label}>
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export interface TooltipProps {
  children: ReactElement;
  content: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  disabled?: boolean;
}

export function Tooltip({ children, content, side = 'top', disabled = false }: TooltipProps) {
  if (disabled || !content) return children;
  return <TooltipPrimitive.Provider delayDuration={350}>
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content side={side} sideOffset={6} collisionPadding={8} className="sv-tooltip">
          {content}
          <TooltipPrimitive.Arrow className="sv-tooltip__arrow" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  </TooltipPrimitive.Provider>;
}
